import {
  Box3,
  BufferGeometry,
  Group,
  Material,
  Mesh,
  Object3D,
  Scene,
  Vector3,
} from "three";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import {
  assetUrl,
  type CameraPreset,
  type MillimetrePoint,
  type ReviewLayerId,
  type ReviewScene,
  type ReviewSceneLayer,
} from "../shared/reviewScene";

const MILLIMETRES_TO_METRES = 0.001;

/** Maps authoritative CAD metadata (X right, Y rear, Z up) into Three's Y-up scene. */
export function cadPointToScene(point: MillimetrePoint, target = new Vector3()): Vector3 {
  return target.set(
    point[0] * MILLIMETRES_TO_METRES,
    point[2] * MILLIMETRES_TO_METRES,
    -point[1] * MILLIMETRES_TO_METRES,
  );
}

export function cadDirectionToScene(direction: MillimetrePoint, target = new Vector3()): Vector3 {
  return target.set(direction[0], direction[2], -direction[1]).normalize();
}

export interface SceneVisibility {
  shell: boolean;
  coreInterior: boolean;
  linings: boolean;
  insulation: boolean;
}

interface RegisteredNode {
  object: Object3D;
  layer: ReviewLayerId;
  defaultVisible: boolean;
}

interface LoadedLayer {
  scene: Object3D;
  assembly: Object3D;
  partCount: number;
}

export class AssetIntegrityError extends Error {
  constructor(path: string) {
    super(`The generated model '${path}' does not match the hash in review_scene.json`);
    this.name = "AssetIntegrityError";
  }
}

export class ReviewSceneRegistry {
  readonly bounds = new Box3();
  readonly center = new Vector3();
  readonly size = new Vector3();

  /**
   * Stable parent for planner geometry authored in canonical millimetres/Z-up.
   * Review GLBs are not placed here: they already contain their metres/Y-up transform.
   */
  readonly coordinateRoot = new Group();

  #scene: Scene;
  #loader = new GLTFLoader();
  #registeredNodes = new Map<string, RegisteredNode>();
  #layerScenes = new Map<ReviewLayerId, LoadedLayer>();

  constructor(scene: Scene) {
    this.#scene = scene;
    this.coordinateRoot.name = "canonical_mm_z_up";
    this.coordinateRoot.rotation.x = -Math.PI / 2;
    this.coordinateRoot.scale.setScalar(MILLIMETRES_TO_METRES);
    this.#scene.add(this.coordinateRoot);
  }

  async load(manifest: ReviewScene, manifestUrl: URL): Promise<void> {
    this.disposeLayers();
    const entries = Object.entries(manifest.layers) as [ReviewLayerId, ReviewSceneLayer][];
    const loaded = await Promise.all(
      entries.map(async ([id, definition]) => [id, await this.#loadLayer(id, definition, manifestUrl)] as const),
    );

    for (const [id, layer] of loaded) {
      this.#layerScenes.set(id, layer);
      this.#scene.add(layer.scene);
    }

    this.bounds.makeEmpty();
    for (const layer of this.#layerScenes.values()) this.bounds.expandByObject(layer.scene);
    if (this.bounds.isEmpty()) throw new Error("The generated review model contains no visible geometry");
    this.bounds.getCenter(this.center);
    this.bounds.getSize(this.size);
  }

  get partCounts(): Readonly<Record<ReviewLayerId, number>> {
    return {
      shell: this.#layerScenes.get("shell")?.partCount ?? 0,
      core_interior: this.#layerScenes.get("core_interior")?.partCount ?? 0,
    };
  }

  applyCamera(camera: CameraPreset, targetPosition: Vector3, targetLookAt: Vector3, targetUp: Vector3): void {
    cadPointToScene(camera.position_mm, targetPosition);
    cadPointToScene(camera.target_mm, targetLookAt);
    cadDirectionToScene(camera.up, targetUp);
  }

  applyVisibility(visibility: SceneVisibility, hiddenNodes: readonly string[]): void {
    this.#layerScenes.get("shell")!.scene.visible = visibility.shell;
    this.#layerScenes.get("core_interior")!.scene.visible = visibility.coreInterior;

    for (const { object, layer, defaultVisible } of this.#registeredNodes.values()) {
      if (object === this.#layerScenes.get(layer)?.assembly) continue;
      object.visible = defaultVisible;
      if (layer === "core_interior") {
        const name = object.name.toLowerCase();
        if (!visibility.linings && isLining(name)) object.visible = false;
        if (!visibility.insulation && isInsulationOrMembrane(name)) object.visible = false;
      }
    }

    for (const nodeName of hiddenNodes) {
      const registered = this.#registeredNodes.get(nodeName);
      if (registered) registered.object.visible = false;
    }
  }

  dispose(): void {
    this.disposeLayers();
    this.coordinateRoot.removeFromParent();
  }

  disposeLayers(): void {
    for (const layer of this.#layerScenes.values()) {
      layer.scene.removeFromParent();
      disposeObject(layer.scene);
    }
    this.#layerScenes.clear();
    this.#registeredNodes.clear();
    this.bounds.makeEmpty();
  }

  async #loadLayer(
    id: ReviewLayerId,
    definition: ReviewSceneLayer,
    manifestUrl: URL,
  ): Promise<LoadedLayer> {
    const url = assetUrl(manifestUrl, definition.asset);
    const response = await fetch(url);
    if (!response.ok) throw new Error(`${definition.asset.path} request failed with HTTP ${response.status}`);
    const buffer = await response.arrayBuffer();
    await verifySha256(buffer, definition.asset.sha256, definition.asset.path);
    const gltf = await this.#loader.parseAsync(buffer, new URL(".", url).href);
    const assembly = gltf.scene.getObjectByName(definition.root_node);
    if (!assembly) {
      disposeObject(gltf.scene);
      throw new Error(`${definition.asset.path} is missing required root node '${definition.root_node}'`);
    }

    gltf.scene.name = `${id}_immutable_asset`;
    prepareMeshes(gltf.scene);
    let partCount = 0;
    for (const part of assembly.children) {
      if (!part.name) continue;
      partCount += 1;
      registerNodeTree(part, id, this.#registeredNodes);
    }
    this.#registeredNodes.set(assembly.name, {
      object: assembly,
      layer: id,
      defaultVisible: assembly.visible,
    });
    return { scene: gltf.scene, assembly, partCount };
  }
}

function registerNodeTree(
  root: Object3D,
  layer: ReviewLayerId,
  registry: Map<string, RegisteredNode>,
): void {
  root.traverse((object) => {
    if (!object.name) return;
    object.userData.reviewLayer = layer;
    object.userData.immutableCore = true;
    registry.set(object.name, { object, layer, defaultVisible: object.visible });
  });
}

function prepareMeshes(root: Object3D): void {
  root.traverse((object) => {
    if (!(object instanceof Mesh)) return;
    object.castShadow = true;
    object.receiveShadow = true;
    if (Array.isArray(object.material)) object.material = object.material.map((material) => material.clone());
    else object.material = object.material.clone();
  });
}

function isLining(name: string): boolean {
  return name.includes("lining") || name.includes("plywood");
}

function isInsulationOrMembrane(name: string): boolean {
  return name.includes("insulation") || name.includes("membrane") || name.includes("moisture");
}

async function verifySha256(buffer: ArrayBuffer, expected: string, path: string): Promise<void> {
  if (!globalThis.crypto?.subtle) return;
  const digest = await crypto.subtle.digest("SHA-256", buffer);
  const actual = [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
  if (actual !== expected.toLowerCase()) throw new AssetIntegrityError(path);
}

function disposeObject(root: Object3D): void {
  root.traverse((object) => {
    if (!(object instanceof Mesh)) return;
    (object.geometry as BufferGeometry).dispose();
    const materials = Array.isArray(object.material) ? object.material : [object.material];
    for (const material of materials as Material[]) material.dispose();
  });
}
